import React, { PureComponent } from 'react'
import { Trans } from '@lingui/react'
import SVGInline from 'react-svg-inline'
import classNames from 'classnames'

import urlFor, { QueryParams } from 'shared/tools/url-helper'
import subscriptionHelper from 'client/shared/helpers/subscription-helper'
import uiBox, { DecoratorProps } from 'client/shared/decorators/ui-box'

import { ReadablePrice } from 'client/shared/blocks/readable-price'
import StyledButton from 'client/shared/blocks/styled-button'
import { StyledBadge } from 'client/shared/blocks/badge'
import Spacer from 'client/shared/blocks/spacer'

import { SubscriptionContext } from 'client/shared/helpers/subscription-helper'
import TaxesComment from 'client/bookmate/blocks/subscription-form/taxes-comment'

import DiscountBadgeShape from './discount-badge.svg'

import './product-card.styl'

import { Product, ProductButton } from 'client/shared/types/subscription'
import { Locale } from 'client/shared/types/current-user'

type Props = {
  product: Product
  single?: boolean
  locale: Locale
  query?: QueryParams
} & DecoratorProps

const getPath = (product: Product, path: string, query?: QueryParams) =>
  subscriptionHelper.isProductUpgradable(product)
    ? urlFor.subscriptionUpgrade(product.id, path, query)
    : urlFor.subscriptionKind(product.id, path, query)

export const IconsRow = (product: Product): JSX.Element[] =>
  product.icons.map(({ label: iconLabel, url }) => (
    <img
      key={iconLabel}
      className="product-card__icon"
      title={iconLabel}
      alt={iconLabel}
      src={url}
    />
  ))
class ProductCard extends PureComponent<Props> {
  static contextType = SubscriptionContext

  getButtonData(): ProductButton {
    return this.props.product.button
  }

  render(): JSX.Element {
    const className = classNames('product-card', {
      'product-card_single': this.props.single,
    })

    const { discount_percent, label } = this.props.product
    const { text: labelText, color } = label

    const { product, query } = this.props
    const buttonData = this.getButtonData()
    const style = {
      color: buttonData.text_color,
      backgroundColor: buttonData.background_color,
      marginBottom: '14px',
    }

    return (
      <div className={className}>
        <div className="product-card__top">
          <div className="product-card__label">
            <StyledBadge color={color}>{labelText}</StyledBadge>
          </div>
          {discount_percent && (
            <DiscountBadge discountPercent={discount_percent} />
          )}
        </div>
        {this.renderMain()}
        <div className="product-card__bottom">
          <div className="product-card__icons">{IconsRow(product)}</div>
          <Spacer size={20} />
          <div className="product-card__button">
            <StyledButton
              style={style}
              path={getPath(product, this.context, query)}
            >
              <span dangerouslySetInnerHTML={{ __html: buttonData.text }} />
            </StyledButton>
          </div>
        </div>
      </div>
    )
  }

  renderMain(): JSX.Element {
    const {
      price,
      currency,
      price_comment: priceComment,
      advantages_description,
    } = this.props.product

    return (
      <div className="product-card__main">
        {advantages_description && (
          <ul className="product-card__description">
            {advantages_description.map(advantage => (
              <li key={advantage}>{advantage}</li>
            ))}
          </ul>
        )}
        <span className="product-card__price">
          <ReadablePrice kind="monthly" price={price} currency={currency} />
          <div className="product-card__header-comment">{priceComment}</div>
          <TaxesComment
            withoutMarginTop
            className="product-card__header-comment"
          />
        </span>
      </div>
    )
  }
}

class ProductCardHorizontal extends PureComponent<Props & DecoratorProps> {
  static contextType = SubscriptionContext

  getButtonData(): ProductButton {
    return this.props.product.button
  }

  render(): JSX.Element {
    return (
      <div className="product-card_horizontal">
        {this.renderDiscount()}
        {this.renderTop()}
        <Spacer size={24} />
        {this.renderButton()}
      </div>
    )
  }

  renderDiscount(): JSX.Element | null {
    const { discount_percent } = this.props.product
    if (!discount_percent) return null

    return <DiscountBadge discountPercent={discount_percent} />
  }

  renderTop(): JSX.Element {
    const { isMobileSize } = this.props

    if (isMobileSize()) {
      return this.renderMobileCardTop()
    } else {
      return this.renderDesktopCardTop()
    }
  }

  renderMobileCardTop(): JSX.Element {
    return (
      <div className="product-card_horizontal__top">
        {this.renderLabel()}
        <Spacer />
        {this.renderPrice()}
        <Spacer />
        <div className="product-card_horizontal__description">
          {this.props.product.description}
        </div>
      </div>
    )
  }

  renderDesktopCardTop(): JSX.Element {
    return (
      <div className="product-card_horizontal__top">
        <div className="product-card_horizontal__top-left">
          {this.renderLabel()}
          <div className="product-card_horizontal__description">
            {this.props.product.description}
          </div>
        </div>
        <div className="product-card_horizontal__top-right">
          {this.renderPrice()}
        </div>
      </div>
    )
  }

  renderLabel(): JSX.Element {
    const { text: labelText, color } = this.props.product.label

    return (
      <div className="product-card_horizontal__label">
        <StyledBadge color={color}>{labelText}</StyledBadge>
      </div>
    )
  }

  renderPrice(): JSX.Element {
    const { price, currency, duration, price_comment } = this.props.product

    return (
      <div className="product-card_horizontal__price">
        <div className="product-card_horizontal__amount">
          <ReadablePrice kind="short" price={price} currency={currency} />
          <div className="product-card_horizontal__period">
            <Trans id={`plan.per_${duration}`} />
            {price_comment && (
              <div className="product-card_horizontal__price-comment">
                {price_comment}
              </div>
            )}
            <TaxesComment className="product-card_horizontal__period" />
          </div>
        </div>
      </div>
    )
  }

  renderButton(): JSX.Element {
    const { product, query } = this.props
    const buttonData = this.getButtonData()
    const style = {
      display: 'inline-block',
      width: 'auto',
      color: buttonData.text_color,
      backgroundColor: buttonData.background_color,
    }

    return (
      <div className="product-card__button">
        <StyledButton
          style={style}
          path={getPath(product, this.context, query)}
        >
          <span dangerouslySetInnerHTML={{ __html: buttonData.text }} />
        </StyledButton>
      </div>
    )
  }
}

export function DiscountBadge({
  discountPercent,
}: {
  discountPercent: number
}): JSX.Element {
  return (
    <div className="product-card__discount">
      <SVGInline svg={DiscountBadgeShape} />
      <div className="product-card__discount-text">-{discountPercent}%</div>
    </div>
  )
}

const wrappedProductCardHorizontal = uiBox(ProductCardHorizontal)
export { wrappedProductCardHorizontal as ProductCardHorizontal }

export default ProductCard
